import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import * as yup from 'yup';
import style from './style.module.scss';
import { NotificationManager } from '../../components/notification-manager';
import { Images } from '../../components/shared-game-components/game-images';
import { PageTopBar } from '../../components/page-top-bar';
import { SaveButton, DeleteButton } from '../../components/action-buttons';
import { useAuthStore, useRechargeStore } from '../../contexts/mobx';
import { useUnsavedChanges } from '../../contexts/unsaved-changes';
import { useNavigation } from '../../utils/use-navigation';
import { useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { RechargeForm, UpsertRechargeImage } from '../../stores/recharge';
import { Card, Input, Switch } from 'antd';

const YupParams = yup.object({
    action: yup.string().oneOf(['create', 'edit']).required(),
    id: yup
        .string()
        .test('shouldBeDefined', 'Id should be defined', function (v) {
            const action = this.resolve(yup.ref('action'));
            return !(v === undefined && action !== 'create');
        }),
});

type Params = yup.InferType<typeof YupParams>;

const YupForm = yup.object({
    image: yup.object({
        fr: yup.string().required(),
        en: yup.string().required(),
    }),
    active: yup.boolean().required(),
    place: yup.string().required(),
    type: yup.string().required(),
});

export const RechargeImagePage = observer(() => {
    const params = useParams<Params>();
    const store = useRechargeStore();

    const authStore = useAuthStore();
    const navigation = useNavigation();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }

    const formik = useFormik<RechargeForm>({
        initialValues: {
            active: false,
            place: '',
            type: '',
            image: {
                fr: '',
                en: '',
            },
        },
        validateOnMount: true,
        validationSchema: YupForm,
        onSubmit: () => void 0,
    });

    const saveRecharge = useCallback(async () => {
        const payload = YupForm.validateSync(formik.values, {
            stripUnknown: true,
        });
        if (params.action === 'edit') {
            await store.updateRechargeImage(
                Number(params.id!),
                payload as UpsertRechargeImage
            );
        } else {
            await store.createRechargeImage(payload as UpsertRechargeImage);
        }
    }, [formik.values, params.action, params.id, store]);

    const { clearDirty } = useUnsavedChanges({
        dirty: formik.dirty,
        onSave: saveRecharge,
    });

    const createRecharge = useCallback(async () => {
        await saveRecharge();
        clearDirty(() => navigation.goToRechargesImages());
    }, [saveRecharge, clearDirty, navigation]);

    const updateRecharge = useCallback(async () => {
        await saveRecharge();
        clearDirty(() => navigation.goToRechargesImages());
    }, [saveRecharge, clearDirty, navigation]);

    if (!YupParams.isValidSync(params)) {
        navigation.goToHome();
    }

    React.useEffect(() => {
        if (params.action === 'edit') {
            store
                .fetchRechargeImage(params.id!)
                .then((recharge) => {
                    if (recharge) {
                        formik.resetForm({ values: recharge });
                    } else {
                        throw new Error('Recharge image not found');
                    }
                })
                .catch(() => navigation.goToHome());
        }
    }, []);

    const onDeleteConfirm = async () => {
        if (params.id) {
            await store.deleteRechargeImage(Number(params.id));
        } else {
            NotificationManager.showError("Can't delete this recharge image");
        }
        navigation.goToRechargesImages();
    };

    const transformId = params.id ? Number(params.id) : undefined;

    const renderTopBarActions = () => {
        switch (params.action) {
            case 'create':
                return (
                    <SaveButton
                        onClick={createRecharge}
                        loading={store.isCreatingRechargeImages}
                        disabled={!formik.isValid}
                    />
                );
            case 'edit':
                return (
                    <>
                        <DeleteButton
                            onClick={onDeleteConfirm}
                            loading={store.isDeletingRechargeImages}
                        />
                        <SaveButton
                            onClick={updateRecharge}
                            loading={store.isUpdatedRechargeImages}
                            disabled={!formik.isValid}
                        />
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <PageTopBar
                title={
                    params.action === 'edit' && transformId
                        ? `Recharge #${transformId}`
                        : 'New Recharge'
                }
                goBack={{
                    callback: () => navigation.goToRechargesImages(),
                    text: 'Back to list',
                }}
            >
                {renderTopBarActions()}
            </PageTopBar>

            <div className="content_with_margins">
                <Card>
                    <div className={style.input_row}>
                        {' '}
                        <span className="left_label">Active</span>
                        <Switch
                            checked={formik.values.active}
                            onChange={(e) => formik.setFieldValue('active', e)}
                            defaultChecked
                        />
                    </div>

                    <div className={style.input_row}>
                        <label>
                            Place:
                            <Input
                                type="text"
                                value={formik.values.place}
                                onChange={(e) =>
                                    formik.setFieldValue(
                                        'place',
                                        e.target.value
                                    )
                                }
                            />
                        </label>
                    </div>

                    <div className={style.input_row}>
                        <label>
                            Type:
                            <Input
                                type="text"
                                value={formik.values.type}
                                onChange={(e) =>
                                    formik.setFieldValue('type', e.target.value)
                                }
                                placeholder="Banner"
                            />
                        </label>
                    </div>

                    <div className={style.input_row}>
                        <Images
                            image={formik.values.image}
                            title="Image"
                            applyStyle={false}
                            onChange={(image) => {
                                formik.setValues({
                                    ...formik.values,
                                    image,
                                });
                            }}
                            onDelete={(image) => {
                                formik.setValues({
                                    ...formik.values,
                                    image,
                                });
                            }}
                        />
                    </div>
                </Card>
            </div>
        </>
    );
});
